import type { StipulationListResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchWorknumberApi } from "../../../../../api/client";
import { mapStipulationList } from "../../../../stipulations/api/mapStipulations";
import { patchStipulationListsForApproval } from "../../../../stipulations/api/patchStipulationLists";
import type { StipulationListView } from "../../../../stipulations/api/types";

/** Input for a recalc retry — the applicant whose deferred recalc to re-invoke. */
export interface RetryWorknumberRecalcInput {
  approvalId: string;
  applicantId: string;
}

/**
 * Maps the retry response's refreshed panel onto a cached stipulation list,
 * re-applying the `openOnly` filter the same way `useApplyStipulationAction`
 * does, since `updatedPanel` always carries the full unfiltered panel.
 * @param {StipulationListResponse} updatedPanel - Refreshed panel from the recalc-retry response.
 * @param {boolean} [openOnly] - Whether this cache entry is filtered to open stips only.
 * @returns {StipulationListView} The patched list.
 */
function patchListForRecalc(updatedPanel: StipulationListResponse, openOnly?: boolean): StipulationListView {
  const mapped = mapStipulationList(updatedPanel);
  return {
    ...mapped,
    items: openOnly ? mapped.items.filter((item) => item.statusDisplay === "open") : mapped.items,
  };
}

/**
 * Mutation hook for the "Update Employer Stips" retry button. Backed by
 * WorkbenchWorknumberApi.retryWorkNumberRecalc (POST .../worknumber/recalc-retry),
 * which re-invokes the purchased-employer-data recalc alone (no vendor call).
 * The endpoint returns HTTP 200 even when the recalc is still deferred/failed,
 * signaling that case via a non-empty `warnings` array — this hook normalizes
 * that into a thrown error so callers can treat "still deferred" and "request
 * failed" identically (re-show the warning banner). On a real success, the
 * response's `updatedPanel` is patched directly into the cached stipulation
 * lists for this approval, so the Stipulations panel reflects the
 * recalculated stips without an extra refetch round trip.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useRetryWorknumberRecalc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ approvalId, applicantId }: RetryWorknumberRecalcInput) => {
      const response = await workbenchWorknumberApi.retryWorkNumberRecalc(approvalId, applicantId);
      const warning = response.data.warnings?.[0];
      if (warning) {
        throw new Error(warning.message ?? warning.reason ?? "stip_recalc_deferred");
      }
      return response.data;
    },
    onSuccess: (data, { approvalId }) => {
      const updatedPanel = data.data?.updatedPanel;
      if (!updatedPanel) {
        return;
      }
      patchStipulationListsForApproval(queryClient, approvalId, (_current, { openOnly }) =>
        patchListForRecalc(updatedPanel, openOnly)
      );
    },
  });
}
